/* Doporučená částka pro každý typ cíle.
 * ============================================================================
 * Metodika majitele, nadiktovaná 24. 9. 2026. Smysl je jediný: poradce nemá
 * u stolu nic počítat. Zeptá se na jedno číslo a cíl se dopočítá sám —
 * a hlavně vyjde REÁLNĚ, ne přehnaně.
 *
 * ⚠️ DOPORUČENÍ NENÍ ROZHODNUTÍ. Vrací se návrh a vždycky k němu i VZOREC,
 * kterým vznikl, aby ho poradce mohl klientovi říct nahlas a klient si ho
 * mohl přepsat. Číslo bez vysvětlení je u stolu bezcenné — klient se zeptá
 * „proč zrovna tolik“ a poradce musí umět odpovědět.
 *
 * ⚠️ CHYBĚJÍCÍ VSTUP = ŽÁDNÉ DOPORUČENÍ, ne nula. Návrh spočítaný z prázdných
 * polí by vypadal jako fakt. Každá funkce proto vrací `null`, když jí chybí
 * to, na čem stojí, a obrazovka místo tlačítka řekne, co doplnit.
 */

/* ⚠️ ŽÁDNÝ IMPORT planSolver / bilance: planSolver importuje odsud
 * (`rezervaCastka`) a bilance importuje planSolver — vznikl by kruh.
 * zavazkyTaby nic z toho nenačítá. (minimumSmrti se tu od 4. 10. 2026 večer
 * nenačítá — cíl dětí je zpátky na modelu studia, viz `vzdelani`.) */
import { DRUH_NA_TAB, HYPOTEKY } from "./zavazkyTaby";

const cislo = (x) => (Number.isFinite(+x) ? +x : 0);
const kladne = (x) => Math.max(0, cislo(x));

/* ── REZERVA ──────────────────────────────────────────────────────────────
 * Rezerva = NEZBYTNÉ výdaje + splátky, a to na 3 / 6 / 12 měsíců. Ne obvyklé
 * výdaje — v krizi klient neplatí restaurace a dovolenou, platí nájem, jídlo
 * a splátky.
 *
 * ⚠️ SPLÁTKY PATŘÍ DO ZÁKLADU. Exekutor nepočká, až se klient vzpamatuje;
 * hypotéka běží dál. Rezerva, která je nepokrývá, nevydrží ani měsíc.
 *
 * ── JEDEN PŘEPÍNAČ, TŘI VOLBY (majitel, 25. 9. 2026) ─────────────────────
 * Jeho slova: „rezerva buď 3 - 6 - 12 měsíců, no to je jedno.“
 *
 * Do 25. 9. 2026 se tu základ ROZDĚLOVAL na dvě části (nezbytné výdaje
 * 6 měsíců, splátky samostatně 12 / 18 / 24 / 36) — to byla naše interpretace
 * jeho starší věty o závazcích, ne jeho zadání. Upřesnil ho: počet měsíců je
 * JEDEN a platí na CELÝ základ, jak to bylo původně.
 *
 * ⚠️ VÝCHOZÍ ZŮSTÁVÁ 6. Je to číslo, se kterým appka počítala vždy, takže se
 * uzavřené plány bez kliknutí poradce nepohnou ani o korunu (regresní zámek
 * v lib/__tests__/rezervaMesicu.test.js). Poradce si 3 nebo 12 klikne sám
 * přímo u cíle rezervy.
 */
export const REZERVA_MIN_MESICU = 3;
export const REZERVA_DOPORUC_MESICU = 6;

/** Volby přepínače u cíle rezervy — celý základ na 3 / 6 / 12 měsíců. */
export const REZERVA_MESICU_VOLBY = Object.freeze([3, 6, 12]);

/** Výchozí počet měsíců = dosavadní chování appky. */
export const REZERVA_MESICU_VYCHOZI = REZERVA_DOPORUC_MESICU;

/**
 * Zvolený počet měsíců rezervy. NEZNÁMÉ ≠ NULA: prázdno, text i nesmysl
 * propadnou na výchozích 6, ne na nulu (nula by rezervu zrušila celou).
 * Mimo volby ze seznamu se hodnota nepřepisuje — poradce smí chtít i 9.
 */
export function mesicuVRezerve(v) {
  if (v === null || v === undefined || v === "") return REZERVA_MESICU_VYCHOZI;
  const n = +v;
  if (!Number.isFinite(n) || n <= 0) return REZERVA_MESICU_VYCHOZI;
  return n;
}

/**
 * Cílová částka rezervy — JEDEN vzorec pro analýzu i pro plán.
 * `planSolver` ho volá pro svůj dopočítaný cíl „Povinná likvidní rezerva“,
 * takže se obě obrazovky nemohou rozejít (dřív měl solver vzorec opsaný).
 * @returns {number} nezaokrouhlená částka; zaokrouhlení si řeší volající
 */
export function rezervaCastka({ nezbytneVydaje, splatky = 0, mesicu } = {}) {
  return (kladne(nezbytneVydaje) + kladne(splatky)) * mesicuVRezerve(mesicu);
}

/* ⚠️ BEZ NEZBYTNÝCH VÝDAJŮ ŽÁDNÉ DOPORUČENÍ (audit A1.3, 3. 10. 2026).
 * Do 3. 10. stačily samotné splátky: vietnamská rodina bez vyplněných
 * nezbytných výdajů dostala „(0 + 16 500) × 6 = 99 000 Kč“ a tlačítko
 * „Doporučit 99 000 Kč“, zatímco panel ukazoval 519 000. Nezadané není nula —
 * obrazovka místo čísla řekne „doplňte nezbytné výdaje“ (`chybi`).
 * `rezervaCastka()` (plán) se nemění: plán si o základu rozhoduje sám. */
export function rezerva({ nezbytneVydaje, splatky = 0, mesicu } = {}) {
  const v = kladne(nezbytneVydaje);
  const s = kladne(splatky);
  if (v <= 0) return null;
  const m = mesicuVRezerve(mesicu);
  /* Dolní hranice pásma jsou 3 měsíce, ale NIKDY víc než zvolený počet —
   * u volby „3 měsíce“ je pásmo jeden bod, ne obrácený interval. */
  const mMin = Math.min(REZERVA_MIN_MESICU, m);
  return {
    castka: Math.round(rezervaCastka({ nezbytneVydaje: v, splatky: s, mesicu: m })),
    min: Math.round(rezervaCastka({ nezbytneVydaje: v, splatky: s, mesicu: mMin })),
    zaklad: Math.round(v + s),
    /* Složky základu zvlášť — obrazovka si z nich složí vzorec s měnou
     * a oddělovači podle jazyka („200 000 Kč nezbytné…“), `vzorec` níž je
     * jen česká záloha bez měny. */
    nezbytne: Math.round(v),
    splatkyZakladu: Math.round(s),
    mesicu: m,
    /* Volby pro přepínač jdou ven z metodiky, ne z komponenty — jinak by si je
     * každá obrazovka napsala po svém a rozešly by se. */
    volby: [...REZERVA_MESICU_VOLBY],
    /* Vzorec, který poradce přečte klientovi nahlas. Jeden součin, ne dva
     * členy: „(nezbytné + splátky) × měsíce“. */
    vzorec: s > 0
      ? `(${Math.round(v)} nezbytné + ${Math.round(s)} splátky) × ${m}`
      : `${Math.round(v)} nezbytné × ${m}`,
  };
}

/* ── BYDLENÍ ──────────────────────────────────────────────────────────────
 * Čistý měsíční příjem × 12 × 9. Devítinásobek ročního příjmu je NAŠE
 * pravidlo plánu pro CENU bydlení, ne předpis ČNB. ČNB vztahuje DTI k DLUHU
 * (doporučení 8× ročního příjmu, horní hranice deaktivovaná — data/config2026.js
 * `HYPO.DTI_VLASTNI`). Do 5. 10. 2026 tu stálo „ČNB doporučuje“ (velká kontrola
 * M1-10): příjem 60 000 → strop ceny 6 480 000, ČNB-doporučení dluhu 5 760 000.
 *
 * ⚠️ JE TO STROP, NE CÍL. Klient si nemá pořídit nejdražší byt, na který
 * dosáhne. Obrazovka jen varuje, když zadaná cena strop překročí.
 *
 * ⚠️ ŽÁDNÉ „DOPORUČIT STROP“ (majitel 4. 10. 2026, 🔴 2: „ok necham tvoje
 * doporučeni“). U domu za 10 mil. nabízelo tlačítko 43,2 mil. — strop není
 * cena, kterou má klient chtít. Proto výsledek NENESE `castka` (tu obrazovka
 * bere jako doporučení s tlačítkem), jen `strop`.
 *
 * `orientacni` = příjem není ze sekce Příjmy, ale z rychlého pole u cíle
 * („orientačně čistý příjem domácnosti“) — číslo je jen pro nasměrování.
 */
export const BYDLENI_NASOBEK_ROCNIHO = 9;

export function bydleni({ prijemMesicne, orientacni = false }) {
  const p = kladne(prijemMesicne);
  if (p <= 0) return null;
  const strop = Math.round(p * 12 * BYDLENI_NASOBEK_ROCNIHO);
  return {
    strop,
    prijem: Math.round(p),
    nasobek: BYDLENI_NASOBEK_ROCNIHO,
    orientacni: !!orientacni,
    vzorec: `${BYDLENI_NASOBEK_ROCNIHO} × 12 × ${Math.round(p)}`,
  };
}

/* ── VZDĚLÁNÍ DĚTÍ ────────────────────────────────────────────────────────
 * Studium v ČR: 15–20 tis. měsíčně × 10 měsíců (akademický rok má deset,
 * ne dvanáct) × 5 let. Zahraničí je dvoj- až trojnásobek.
 *
 * ⚠️ DESET MĚSÍCŮ, NE DVANÁCT. Přes prázdniny student nebydlí v Praze
 * a většinou si přivydělá. Dvanáctka by cíl nafoukla o pětinu.
 *
 * ── POKYN MAJITELE 4. 10. 2026 ~23:00 (doslova) ──────────────────────────
 * „tohle ne ve velke analyze ale jen ve veitnamske.. to predtim bylo studium
 *  15-20 k x 5 let studiu ..vis .. neplet si to“
 * Od rána 4. 10. tu byl „jeden model dítěte“ (náklad 13 000 Kč měsíčně × 12
 * × roky do 23 let, jako minimum krytí smrti). Ve VELKÉ analýze ho majitel
 * nechce — cíl „Budoucnost dětí“ je zase STUDIUM (model ze stavu fb3cf1d3).
 * Náklad na dítě do samostatnosti zůstává jen v Analýze pro Vietnamce
 * (lib/vietnamRodina/cile.js → detiDo22) a v minimu krytí smrti
 * (lib/minimumSmrti.js) — tady se na něj NESAHÁ a nic odtud ho nevolá.
 *
 * Bez zapsaných dětí se počítá jako dřív na JEDNO dítě (`bezDeti: true`,
 * obrazovka řekne „na jedno dítě“); upozornění „cíl pro děti, ale žádné
 * dítě“ (Č16, lib/analyzaV2/cilProDeti.js) běží dál samostatně.
 *
 * ⚠️ KORUNOVÉ KONSTANTY. 20 000 je v Kč. V analýze vedené v jiné měně by
 * z nich bylo „20 000 € měsíčně“ a „Doporučit 1 000 000 €“ — proto se tam
 * (`mena` ≠ CZK) číslo netvrdí a vrací se null. Bez `mena` = koruny (staré
 * volání). Tahle pojistka je NOVÁ, ne návrat: fb3cf1d3 měnu u studia
 * nehlídala a v eurové analýze by ukázala „… = 1 000 000 €“ s tlačítkem.
 * Vlastní norma studia pro SK v € čeká na rozhodnutí majitele.
 */
export const STUDIUM_MESICNE_MIN = 15000;
export const STUDIUM_MESICNE_MAX = 20000;
export const STUDIUM_MESICU_V_ROCE = 10;
export const STUDIUM_LET = 5;
export const STUDIUM_ZAHRANICI_NASOBEK = 2.5;
export const STUDIUM_MENA = "CZK";

/* `pocetDeti` bez výchozí hodnoty: nezadaný počet = „na jedno dítě“ (`bezDeti`), ne tiché 1. */
export function vzdelani({ mesicne = STUDIUM_MESICNE_MAX, zahranici = false, pocetDeti, mena = STUDIUM_MENA } = {}) {
  if (String(mena ?? STUDIUM_MENA).trim().toUpperCase() !== STUDIUM_MENA) return null;
  const m = kladne(mesicne) || STUDIUM_MESICNE_MAX;
  const zadano = Math.round(cislo(pocetDeti));
  const deti = Math.max(1, zadano || 1);
  const naDite = m * STUDIUM_MESICU_V_ROCE * STUDIUM_LET * (zahranici ? STUDIUM_ZAHRANICI_NASOBEK : 1);
  return {
    castka: Math.round(naDite * deti),
    naDite: Math.round(naDite),
    pocet: deti,
    /* Žádné dítě v analýze → počítá se na jedno (jako dřív), obrazovka to řekne. */
    bezDeti: !(zadano > 0),
    mesicne: Math.round(m),
    mesicu: STUDIUM_MESICU_V_ROCE,
    let: STUDIUM_LET,
    zahranici: !!zahranici,
    rozsahCR: [
      STUDIUM_MESICNE_MIN * STUDIUM_MESICU_V_ROCE * STUDIUM_LET * deti,
      STUDIUM_MESICNE_MAX * STUDIUM_MESICU_V_ROCE * STUDIUM_LET * deti,
    ],
    vzorec: `${Math.round(m)}/měs × ${STUDIUM_MESICU_V_ROCE} měsíců × ${STUDIUM_LET} let`
      + (zahranici ? ` × ${STUDIUM_ZAHRANICI_NASOBEK} (zahraničí)` : "")
      + (deti > 1 ? ` × ${deti} děti` : ""),
  };
}

/* ── AUTO ─────────────────────────────────────────────────────────────────
 * Cena dalšího auta MÍNUS to, co klient utrží za stávající. Nespoří se na
 * celé auto, spoří se na rozdíl.
 *
 * ⚠️ NENADSAZOVAT. Majitel to řekl jasně: „ať to není přehnaný — když bude
 * mít za rok dva víc peněz, udělám novou analýzu." Cíl, který je nafouknutý,
 * sežere prostor ostatním cílům a plán pak nevyjde ani jeden.
 */
export function auto({ cenaNoveho, prodejStavajiciho = 0 }) {
  const c = kladne(cenaNoveho);
  if (c <= 0) return null;
  const odecet = Math.min(kladne(prodejStavajiciho), c);
  return {
    castka: Math.round(c - odecet),
    vzorec: odecet > 0
      ? `${Math.round(c)} nové auto − ${Math.round(odecet)} za stávající`
      : `${Math.round(c)} cena auta`,
  };
}

/* ── RENTA (finanční nezávislost) ─────────────────────────────────────────
 * CELKOVÉ výdaje včetně životního stylu a splátek — ne nezbytné.
 *
 * ⚠️ TOHLE BYLA CHYBA, KTEROU MAJITEL NAŠEL. Doporučení bralo `vydaje2.obvykle`
 * samotné, takže u rodiny s příjmem 80 000 navrhovalo rentu 41 000. Renta ale
 * musí uživit TENTÝŽ ŽIVOT, jaký klient žije teď — se vším, co k němu patří.
 * Majitel to shrnul: „když vydělávají 80 tisíc, renta má být 70–80 tisíc,
 * nebo 70 bez hypotéky."
 *
 * ⚠️ HYPOTÉKA SE DÁ ODEČÍST, A ČASTO SE MÁ. Za 25 let bývá doplacená a klient
 * ji v důchodu platit nebude. Je to ale VOLBA poradce, ne automatika — někdo
 * si v šedesáti bere další hypotéku.
 *
 * `kontrola` je tu proto, aby poradce viděl, že číslo sedí na příjem. Když
 * renta vyjde výrazně pod příjem, něco ve výdajích chybí.
 */
export function renta({ vydajeCelkem, splatky = 0, splatkaHypoteky = 0, bezHypoteky = false, prijem = 0 }) {
  const v = kladne(vydajeCelkem);
  const s = kladne(splatky);
  if (v <= 0 && s <= 0) return null;

  const odecet = bezHypoteky ? Math.min(kladne(splatkaHypoteky), s) : 0;
  const castka = Math.round(v + s - odecet);
  const p = kladne(prijem);

  /* Kontrola na příjem: renta by u běžné rodiny měla vyjít blízko příjmu.
     Výrazně níž znamená, že poradce nemá vyplněné všechny výdaje. */
  let kontrola = null;
  if (p > 0 && castka > 0) {
    const podil = castka / p;
    if (podil < 0.6) {
      kontrola = `Renta je jen ${Math.round(podil * 100)} % příjmu (${Math.round(p)}). Ve výdajích nejspíš něco chybí — klient přece nežije z poloviny toho, co vydělá.`;
    } else if (podil > 1.15) {
      kontrola = `Renta je ${Math.round(podil * 100)} % příjmu. Klient utrácí víc, než vydělá — ověřte výdaje a splátky.`;
    }
  }

  return {
    castka,
    vzorec: bezHypoteky && odecet > 0
      ? `${Math.round(v)} výdaje + ${Math.round(s)} splátky − ${Math.round(odecet)} hypotéka`
      : `${Math.round(v)} výdaje + ${Math.round(s)} splátky`,
    kontrola,
  };
}

/* ── RENTA: HYPOTÉKA, KTERÁ DOBĚHNE DŘÍV, NEŽ RENTA ZAČNE (🔴 1) ──────────
 * Rozhodnutí majitele 4. 10. 2026 („ok necham tvoje doporučeni“): renta se
 * počítá BEZ splátky hypotéky, když hypotéka skončí dřív, než renta začne,
 * JINAK SE SPLÁTKOU. Do té doby nápověda u cíle splátky přičítala vždy
 * (78 500 Kč) a panel „Co z čísel vyplývá“ nikdy (62 000 Kč) — potřebný
 * kapitál vycházel jednou 18,6 a jindy 21 mil. Teď obě místa volají tohle.
 *
 * Hypotéka = řádek z tabu Hypotéky (druh hypotéka, úvěr ze stavebka, americká
 * hypotéka, jiný úvěr na bydlení) nebo „hypot…“ v názvu. Ostatní úvěry
 * (spotřebák, leasing) zůstávaly do 5. 10. 2026 v rentě vždy (viz níž).
 *
 * ⚠️ NEZNÁMÉ ≠ DOPLACENO. Hypotéka bez data konce splácení, nebo renta bez
 * termínu, zůstává V RENTĚ (se splátkou) — slíbit klientovi nižší rentu
 * kvůli hypotéce, o které nevíme, kdy skončí, by bylo podstřelení.
 *
 * ── 5. 10. 2026 (majitel v dotazníku „Ano, počítat bez nich“, M1-15) ──────
 * Potřeba renty = výdaje − VLASTNÍ pravidelné vklady na spoření (v důchodu
 * se do stavebka ani penzijka neposílá; příspěvek zaměstnavatele ve výdajích
 * není) − splátky ÚVĚRŮ, které skončí dřív, než renta začne. Konec se bere
 * z data konce splácení, a když chybí, odhadne se z jistiny (zůstatku),
 * úroku a splátky (anuita, `mesicuDoSplaceni`). Týká se to všech úvěrů,
 * nejen hypotéky — spotřebák doplacený za 4 roky v rentě platit nebudete
 * (dřív zůstával „rozhodnutí se týká hypotéky“). Úvěr, který běží i v rentě,
 * nebo u kterého konec spočítat nejde (chybí úrok / zůstatek), se počítá dál
 * a výsledek to nese v `neznamyKonec` (obrazovka: „doplňte úrok…“).
 * Vzorová rodina z kina 50/30/20: 72 000 → 46 300 Kč, kapitál 21,6 → 13,9 mil.
 */
const jeHypotekaRadek = (z) =>
  DRUH_NA_TAB[String(z?.druh ?? "").trim()] === HYPOTEKY || /hypot/i.test(String(z?.oznaceni ?? ""));

/* Konec splácení: ISO datum („2049-05-31“), jinak samotný rok („2049“ = konec roku). */
const konecSplaceni = (v) => {
  const s = String(v ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const t = Date.parse(s.slice(0, 10));
    return Number.isFinite(t) ? new Date(t) : null;
  }
  if (/^\d{4}$/.test(s)) return new Date(Date.UTC(+s, 11, 31));
  return null;
};

const platnyDatum = (v) => {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v.trim())) {
    const t = Date.parse(v.trim().slice(0, 10));
    return Number.isFinite(t) ? new Date(t) : null;
  }
  return null;
};

/* Úrok v % ročně, jak ho poradce zapsal („5,2“ i 5.2). Prázdno, nula
 * a nesmysl = NEZNÁMÝ: šablona analýzy zakládá řádky s `urok: 0`, takže
 * nula a „nevyplněno“ se nedají rozeznat. Úvěr za 0 % se tím počítá
 * opatrně (zůstane v rentě) — horší chyba by byla slíbit nižší rentu. */
const urokPct = (v) => {
  const n = Number(String(v ?? "").trim().replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n > 0 && n < 100 ? n : null;
};

/**
 * ZA KOLIK MĚSÍCŮ SE ÚVĚR DOPLATÍ — anuita z jistiny (zůstatku), úroku a splátky.
 *   n = −ln(1 − r·P / A) / ln(1 + r),  r = úrok / 12
 * @param {{ jistina: number, urok: number|string, splatka: number }} p
 * @returns {{ stav: "spocteno"|"nikdy"|"chybiUrok"|"chybiZustatek"|"bezSplatky", mesicu: number|null, minMesicu: number|null }}
 *   `nikdy` = splátka nepokryje ani úrok, úvěr nedoběhne;
 *   `chybiUrok` nese `minMesicu` = doba při 0 % (dřív skončit nemůže);
 *   `chybiZustatek` = bez jistiny nejde ani to.
 */
export function mesicuDoSplaceni({ jistina, urok, splatka } = {}) {
  const P = kladne(jistina);
  const A = kladne(splatka);
  if (!(A > 0)) return { stav: "bezSplatky", mesicu: null, minMesicu: null };
  if (!(P > 0)) return { stav: "chybiZustatek", mesicu: null, minMesicu: null };
  const u = urokPct(urok);
  if (u == null) return { stav: "chybiUrok", mesicu: null, minMesicu: P / A };
  const r = u / 100 / 12;
  if (A <= P * r) return { stav: "nikdy", mesicu: null, minMesicu: P / A };
  return { stav: "spocteno", mesicu: -Math.log(1 - (r * P) / A) / Math.log(1 + r), minMesicu: P / A };
}

/* Datum + celé měsíce (UTC, den v měsíci se drží jako v `start`). */
const plusMesicu = (d, m) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + m, d.getUTCDate()));

/**
 * Které splátky zůstanou v rentě.
 * @param {object} p
 * @param {Array} p.zavazky      řádky `zavazkyB` + `zavazkyO`
 * @param {number|string} p.rokyDoRenty  za kolik let renta začne (`cil.roky`); prázdno = neznámo
 * @param {Date|string} [p.kDatu] od kdy se roky počítají (datum analýzy, jinak dnešek)
 * @returns {{ splatky: number, odecet: number, vRente: number, rokDoplaceni: number|null,
 *   neznamyKonec: Array<{ id: any, oznaceni: string, hypoteka: boolean, chybi: "urok"|"zustatek", splatka: number }> }}
 *   splatky = všechny měsíční splátky, odecet = úvěry doplacené před rentou
 *   (od 5. 10. 2026 všechny úvěry, dřív jen hypotéky), vRente = splatky − odecet,
 *   rokDoplaceni = rok konce posledního odečteného úvěru, neznamyKonec = úvěry,
 *   které v rentě zůstaly jen proto, že jejich konec nejde spočítat (a skončit
 *   před rentou by mohly) — obrazovka napíše „doplňte úrok…“.
 */
export function splatkyDoRenty({ zavazky = [], rokyDoRenty, kDatu } = {}) {
  const radky = Array.isArray(zavazky) ? zavazky : [];
  const rokyText = rokyDoRenty === null || rokyDoRenty === undefined ? "" : String(rokyDoRenty).trim();
  const roky = rokyText !== "" && Number.isFinite(+rokyText) && +rokyText >= 0 ? +rokyText : null;
  const od = platnyDatum(kDatu) || new Date();
  const start = roky == null ? null
    : new Date(Date.UTC(od.getUTCFullYear() + Math.floor(roky), od.getUTCMonth(), od.getUTCDate()));
  const mesicuDoRenty = roky == null ? null : Math.floor(roky) * 12;
  let splatky = 0;
  let odecet = 0;
  let rokDoplaceni = null;
  const neznamyKonec = [];
  for (const z of radky) {
    const s = kladne(z && z.splatka);
    if (!(s > 0)) continue;
    splatky += s;
    if (!start) continue;
    /* Jen HYPOTÉKA (vč. úvěru ze stavebka) může z renty vypadnout — rozhodnutí
       4. 10. („renta bez splátky hypotéky, když skončí před rentou“) a souhlas
       5. 10. se týkaly hypotéky. Spotřebák, leasing a karta v rentě zůstávají
       (nové úvěry na auto/vybavení přijdou i v rentě). */
    if (!jeHypotekaRadek(z)) continue;
    /* 1) Zadaný konec splácení rozhoduje (poradce ho ví od banky). */
    const konec = konecSplaceni(z.konec);
    if (konec) {
      if (konec.getTime() <= start.getTime()) {
        odecet += s;
        rokDoplaceni = Math.max(rokDoplaceni ?? 0, konec.getUTCFullYear());
      }
      continue;
    }
    /* 2) Jinak odhad z jistiny, úroku a splátky. */
    const est = mesicuDoSplaceni({ jistina: z.jistina, urok: z.urok, splatka: s });
    if (est.stav === "spocteno") {
      if (est.mesicu <= mesicuDoRenty) {
        odecet += s;
        rokDoplaceni = Math.max(rokDoplaceni ?? 0, plusMesicu(od, Math.ceil(est.mesicu)).getUTCFullYear());
      }
      continue;
    }
    /* Bez úroku: ani při 0 % to do renty nedoběhne → běží v rentě jistě,
       hláška by nic nezměnila. Jinak v rentě „opatrně“ a s hláškou. */
    if (est.stav === "chybiUrok" && est.minMesicu > mesicuDoRenty) continue;
    if (est.stav === "chybiUrok" || est.stav === "chybiZustatek") {
      neznamyKonec.push({
        id: z.id ?? null, oznaceni: String(z.oznaceni ?? ""), hypoteka: jeHypotekaRadek(z),
        chybi: est.stav === "chybiZustatek" ? "zustatek" : "urok", splatka: s,
      });
    }
  }
  return { splatky, odecet, vRente: splatky - odecet, rokDoplaceni, neznamyKonec };
}

/**
 * Která hláška patří k úvěrům s neznámým koncem — klíč věty slovníku
 * (`analyzaCisla.renta.<klic>`), nebo null. Hypotéka má přednost (je to
 * nejčastější a největší splátka), chybějící zůstatek přednost před úrokem
 * (bez zůstatku nepomůže ani úrok).
 * @param {Array} neznamyKonec výstup `splatkyDoRenty` / `rentaSeZavazky`
 * @returns {null | "doplnZustatekHypoteky" | "doplnUrokHypoteky" | "doplnZustatekUveru" | "doplnUrokUveru"}
 */
export function hlaskaKonceUveru(neznamyKonec) {
  const n = Array.isArray(neznamyKonec) ? neznamyKonec : [];
  if (!n.length) return null;
  const hypo = n.filter((x) => x && x.hypoteka);
  const skupina = hypo.length ? hypo : n;
  const zustatek = skupina.some((x) => x && x.chybi === "zustatek");
  if (hypo.length) return zustatek ? "doplnZustatekHypoteky" : "doplnUrokHypoteky";
  return zustatek ? "doplnZustatekUveru" : "doplnUrokUveru";
}

/**
 * Jeden řádek „bez čeho“ u potřeby renty — klíč věty slovníku
 * (`analyzaCisla.renta.<klic>`), nebo null, když se nic neodečetlo.
 * @param {{ vklady?: number, odecetUveru?: number }} r výstup `rentaSeZavazky`
 */
export function radekBezCeho(r) {
  const o = r && typeof r === "object" ? r : {};
  const sp = kladne(o.odecetUveru) > 0;
  const vk = kladne(o.vklady) > 0;
  if (sp && vk) return "bezSplatekISporeni";
  if (sp) return "bezSplatek";
  if (vk) return "bezSporeni";
  return null;
}

/**
 * RENTA SE ZÁVAZKY — jediný výpočet potřeby renty (panel „Co z čísel
 * vyplývá“, nápověda u cíle, kina, plán přes `potrebaRenty` v potrebaRenty.js).
 * @param {object} p
 * @param {number} p.vydajeCelkem  dnešní výdaje domácnosti (vč. vlastních vkladů na spoření)
 * @param {number} [p.vlastniVklady] vlastní pravidelné vklady na spoření bez
 *   příspěvku zaměstnavatele — v rentě se neplatí (5. 10. 2026); víc než výdaje
 *   se neodečte
 * @returns {null | {castka, vzorec, kontrola, vydaje, vydajeDnes, vklady, splatky,
 *   odecetUveru, odecetHypoteky, rokDoplaceni, neznamyKonec}}
 *   `vydaje` = výdaje bez vlastních vkladů (s tímhle číslem počítají věty),
 *   `splatky` = splátky, které v rentě zůstávají, `odecetUveru` = splátky
 *   úvěrů doplacených před rentou (`odecetHypoteky` = totéž, starý název),
 *   `castka` = vydaje + splatky.
 */
export function rentaSeZavazky({ vydajeCelkem, vlastniVklady = 0, prijem = 0, zavazky = [], rokyDoRenty, kDatu } = {}) {
  const sp = splatkyDoRenty({ zavazky, rokyDoRenty, kDatu });
  const vDnes = kladne(vydajeCelkem);
  /* Kontrola na příjem (renta < 60 % / > 115 % příjmu) hlídá, jestli ve
     VÝDAJÍCH něco nechybí — měří proto DNEŠNÍ odtok (výdaje + všechny
     splátky), ne potřebu v rentě: rodina, které z renty vypadne hypotéka
     a spoření, nemá „nejspíš něco chybí“. */
  const dnes = renta({ vydajeCelkem: vDnes, splatky: sp.splatky, prijem });
  if (!dnes) return null;
  const vklady = Math.min(kladne(vlastniVklady), vDnes);
  const vydaje = vDnes - vklady;
  const castka = Math.round(vydaje + sp.vRente);
  const casti = [`${Math.round(vDnes)} výdaje`];
  if (vklady > 0) casti.push(`− ${Math.round(vklady)} spoření`);
  if (sp.splatky > 0) casti.push(`+ ${Math.round(sp.splatky)} splátky`);
  if (sp.odecet > 0) casti.push(`− ${Math.round(sp.odecet)} splátky doplacené před rentou`);
  return {
    castka,
    vzorec: casti.join(" "),
    kontrola: dnes.kontrola,
    vydaje: Math.round(vydaje),
    vydajeDnes: Math.round(vDnes),
    vklady: Math.round(vklady),
    splatky: Math.round(sp.vRente),
    odecetUveru: Math.round(sp.odecet),
    odecetHypoteky: Math.round(sp.odecet),
    rokDoplaceni: sp.rokDoplaceni,
    neznamyKonec: sp.neznamyKonec,
  };
}

/**
 * Doporučení podle typu cíle. Vrací `null`, když chybí vstupy.
 *
 * @param {object} cil    cíl z analýzy (typ, popis, hodnota)
 * @param {object} ctx    { prijemMesicne, vydajeCelkem, nezbytneVydaje,
 *                          splatky, splatkaHypoteky, pocetDeti, mesicu, mena }
 *   `mena` = kód měny dat analýzy (studium dětí jen v korunách; prázdno = Kč);
 *   `mesicu` = volba poradce u rezervy (3 / 6 / 12 měsíců celého základu);
 *   prázdno → `REZERVA_MESICU_VYCHOZI`, tedy 6.
 */
export function doporuceniProCil(cil = {}, ctx = {}) {
  const typ = cil.typ || "";
  const popis = String(cil.popis || "").toLowerCase();

  if (typ === "Rezerva" || /rezerv/.test(popis)) {
    /* Jeden výpočet, ne dva: `rezerva()` se dřív volala dvakrát (podruhé jen
     * kvůli `chybi`), takže hrozilo, že se obě volání rozejdou. */
    const r = rezerva({ nezbytneVydaje: ctx.nezbytneVydaje, splatky: ctx.splatky, mesicu: ctx.mesicu });
    return { druh: "rezerva", ...(r || {}), chybi: r ? null : "nezbytné výdaje" };
  }
  if (typ === "Finanční nezávislost" || cil.mesicne) {
    /* 🔴 1 (4. 10. 2026): se závazky v ctx rozhoduje `splatkyDoRenty` —
       hypotéka doplacená před rentou se z renty vynechá. Bez nich staré
       chování (volající, které závazky nezná). */
    if (Array.isArray(ctx.zavazky)) {
      /* `vlastniVklady` (5. 10. 2026): vlastní vklady na spoření se z renty
         vyndají — volající s daty analýzy má raději `potrebaRenty`. */
      const r = rentaSeZavazky({ vydajeCelkem: ctx.vydajeCelkem, vlastniVklady: ctx.vlastniVklady, prijem: ctx.prijemMesicne, zavazky: ctx.zavazky, rokyDoRenty: cil.roky, kDatu: ctx.kDatu });
      return { druh: "renta", ...(r || {}), chybi: r ? null : "celkové výdaje" };
    }
    const r = renta(ctx);
    return { druh: "renta", ...(r || {}), chybi: r ? null : "celkové výdaje" };
  }
  /* REKONSTRUKCE NENÍ KOUPĚ BYTU (audit A1.4, 3. 10. 2026). Strop 9× ročního
   * příjmu je pravidlo pro hypotéku na vlastní bydlení; u rekonstrukce za
   * 600 000 nabízel „Doporučit 10 080 000 Kč (strop ČNB)“. Rekonstrukce proto
   * nedostává strop ani tlačítko — částku řekne klient. */
  if (typ === "Rekonstrukce") return null;
  if (typ === "Bydlení") {
    /* Skutečné příjmy mají přednost; orientační (rychlé pole u cíle) jen když
       skutečné chybí — a výsledek to nese (`orientacni`). */
    const b = bydleni({ prijemMesicne: ctx.prijemMesicne })
      || bydleni({ prijemMesicne: ctx.prijemOrientacni, orientacni: true });
    return { druh: "bydleni", ...(b || {}), chybi: b ? null : "čistý příjem" };
  }
  if (typ === "Budoucnost dětí") {
    /* Studium (pokyn majitele 4. 10. 2026 ~23:00): počet dětí z analýzy,
       `ctx.mena` = měna dat analýzy — mimo koruny se číslo netvrdí. */
    const v = vzdelani({ pocetDeti: ctx.pocetDeti, mena: ctx.mena });
    return { druh: "vzdelani", ...(v || {}), chybi: v ? null : "měna" };
  }
  if (typ === "Auto") {
    const a = auto(ctx);
    return { druh: "auto", ...(a || {}), chybi: a ? null : "cena auta" };
  }
  return null;
}
